/**
 * Button.
 *
 * Variants exist so that colour keeps meaning direction. There is no
 * "success" green button and no "sell" red button: green and red are
 * reserved for market direction, and a green Save button would make the
 * chart harder to read. Interaction is `accent`.
 *
 * `danger` is the exception, and it is deliberately expensive to reach for:
 * it is for halting, retiring and overriding — actions that should look
 * like what they are.
 */

import { Loader2 } from 'lucide-react';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { cn } from '@/lib/cn';

import type { ControlSize, IconComponent } from './types';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'outline'
  | 'danger'
  | 'warn';

/** The shared control scale. See `components/ui/types.ts`. */
export type ButtonSize = ControlSize;

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-accent text-accent-fg border border-accent hover:brightness-110 active:brightness-95',
  secondary:
    'bg-surface-2 text-hi border border-border hover:bg-surface-3 hover:border-border-strong',
  ghost: 'bg-transparent text-mid border border-transparent hover:bg-surface-2 hover:text-hi',
  outline:
    'bg-transparent text-hi border border-border-strong hover:bg-surface-2 hover:border-accent',
  danger:
    'bg-danger text-danger-fg border border-danger hover:brightness-110 active:brightness-95',
  warn: 'bg-warn text-warn-fg border border-warn hover:brightness-110 active:brightness-95',
};

const SIZES: Record<ButtonSize, string> = {
  xs: 'h-11 px-2 text-[11px] gap-1 rounded md:h-[22px]',
  sm: 'h-11 px-2.5 text-xs gap-1.5 rounded md:h-[26px] md:text-[11px]',
  md: 'h-11 px-3 text-[13px] gap-1.5 rounded-md md:h-[28px] md:text-[12px]',
  lg: 'h-11 px-4 text-sm gap-2 rounded-md md:h-[34px] md:text-[13px]',
};

const ICON_SIZES: Record<ButtonSize, number> = { xs: 12, sm: 13, md: 14, lg: 16 };

/**
 * @param variant   Visual weight. Colour never means direction here — see
 *                  the file header.
 * @param size      The shared control scale: `xs` | `sm` | `md` | `lg`.
 * @param icon      Leading icon, as a *component* so it can be sized to the
 *                  button's scale. `iconAfter` for trailing.
 * @param loading   Spinner, `aria-busy`, and disabled. A button that can be
 *                  pressed twice while a mutation is in flight is a button
 *                  that can place two orders.
 * @param iconOnly  Square, label-free. **Requires `aria-label`.**
 */
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** A lucide icon component, rendered before the label. */
  icon?: IconComponent;
  iconAfter?: IconComponent;
  /** Shows a spinner and disables the button. */
  loading?: boolean;
  /** Square, label-free. Pass `aria-label` — this is not optional. */
  iconOnly?: boolean;
  fullWidth?: boolean;
  children?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    icon: Icon,
    iconAfter: IconAfter,
    loading = false,
    iconOnly = false,
    fullWidth = false,
    className,
    disabled,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  const iconSize = ICON_SIZES[size];

  // An icon-only button with no accessible name is a control a screen reader
  // announces as "button". The prop comment has always said `aria-label` is
  // not optional; this is what makes that true rather than aspirational.
  if (import.meta.env.DEV && iconOnly && !rest['aria-label'] && !rest['aria-labelledby']) {
    console.error('[Button] iconOnly requires an aria-label.', { title: rest.title });
  }

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex select-none items-center justify-center whitespace-nowrap font-medium',
        'transition-colors duration-100',
        'disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:brightness-100',
        SIZES[size],
        VARIANTS[variant],
        iconOnly && 'aspect-square px-0',
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? (
        <Loader2 size={iconSize} className="animate-spin" aria-hidden />
      ) : (
        Icon && <Icon size={iconSize} aria-hidden />
      )}
      {!iconOnly && children}
      {!iconOnly && !loading && IconAfter && <IconAfter size={iconSize} aria-hidden />}
    </button>
  );
});

/**
 * Segmented control. For timeframe pickers and light/dark/system.
 *
 * `onChange` receives the value, matching Select/Tabs/Toggle. Keyboard
 * behaviour matches `Tabs`: one tab stop for the whole group, arrows move
 * within it, Home/End jump to the ends — which is what `role="radiogroup"`
 * promises a screen-reader user, and what a timeframe switcher needs to be
 * usable without a mouse.
 */
export interface ButtonGroupOption<T extends string> {
  value: T;
  label: string;
  icon?: IconComponent;
  title?: string;
  disabled?: boolean;
  /**
   * Selectable, but carrying a consequence the user should see before
   * clicking.
   *
   * Distinct from `disabled` on purpose. `disabled` says "you may not"; this
   * says "you may, and here is what it costs". The leverage row uses it for
   * settings above the level the symbol's stops tolerate — those runs are
   * legitimate and the owner asked for the range, but they refuse setups, and
   * a silently thinner report is the outcome this exists to prevent. Always
   * pair it with a `title` that names the consequence.
   */
  flagged?: boolean;
}

export interface ButtonGroupProps<T extends string> {
  options: ReadonlyArray<ButtonGroupOption<T>>;
  value: T;
  onChange: (value: T) => void;
  size?: ButtonSize;
  className?: string;
  'aria-label'?: string;
}

export function ButtonGroup<T extends string>({
  options,
  value,
  onChange,
  size = 'sm',
  className,
  'aria-label': ariaLabel,
}: ButtonGroupProps<T>) {
  const iconSize = ICON_SIZES[size];

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    const enabled = options.filter((option) => !option.disabled);
    if (enabled.length === 0) return;
    const index = enabled.findIndex((option) => option.value === value);
    let next = -1;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      next = (index + 1) % enabled.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      next = (index - 1 + enabled.length) % enabled.length;
    } else if (event.key === 'Home') {
      next = 0;
    } else if (event.key === 'End') {
      next = enabled.length - 1;
    }
    if (next < 0) return;
    event.preventDefault();
    const target = enabled[next];
    if (!target) return;
    onChange(target.value);
    event.currentTarget
      .querySelector<HTMLButtonElement>(`[data-option="${CSS.escape(target.value)}"]`)
      ?.focus();
  };

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
      className={cn(
        'inline-flex items-center gap-px rounded border border-border bg-surface-2 p-px',
        className,
      )}
    >
      {options.map((option) => {
        const active = option.value === value;
        const Icon = option.icon;
        return (
          <button
            key={option.value}
            data-option={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            tabIndex={active ? 0 : -1}
            title={option.title ?? option.label}
            disabled={option.disabled}
            onClick={() => onChange(option.value)}
            className={cn(
              'inline-flex items-center justify-center gap-1 rounded-[3px] font-medium transition-colors',
              'disabled:cursor-not-allowed disabled:opacity-40',
              size === 'xs'
                ? 'h-11 px-1.5 text-[10px] md:h-5'
                : 'h-11 px-2 text-[11px] md:h-6',
              active
                ? 'bg-accent text-accent-fg'
                : 'text-mid hover:bg-surface-3 hover:text-hi',
              // Readable in both states: a flagged option that happens to be
              // selected must still look flagged, or the warning disappears
              // exactly when it starts applying.
              option.flagged &&
                (active
                  ? 'bg-warn text-[color:var(--surface-1)]'
                  : 'text-warn ring-1 ring-inset ring-warn/50'),
            )}
          >
            {Icon && <Icon size={iconSize} aria-hidden />}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
